/**
 * Firebase Auth Client
 * LegalSyncUp - Swiss Legal Compliance Platform
 *
 * FIREBASE AUTH - ENTERPRISE IDENTITY PROVIDER
 * =============================================
 * Firebase Auth with Google Cloud Identity Platform provides enterprise-grade
 * identity management for multi-tenant scenarios and SSO integrations.
 *
 * SUPPORTED AUTH METHODS:
 * - Email/password authentication
 * - OAuth providers (Google, GitHub)
 * - Enterprise SSO via SAML/OIDC (Identity Platform)
 *
 * USAGE:
 * ```tsx
 * import { firebaseAuth, signInWithEmail, signOut } from '@/lib/firebase/auth';
 *
 * // Sign in with email
 * await signInWithEmail('user@example.com', 'password');
 *
 * // Sign in with Google (enterprise SSO)
 * await signInWithGoogle();
 *
 * // Sign out
 * await signOut();
 * ```
 *
 * ACTIVATION: Set VITE_USE_FIREBASE_AUTH=true
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  onAuthStateChanged,
  type User,
  type UserCredential,
  type AuthError,
} from 'firebase/auth';
import { getFirebaseAuth, isFirebaseConfigured } from './config';

// Export auth instance getter
export const firebaseAuth = getFirebaseAuth;

// Provider instances
const googleProvider = new GoogleAuthProvider();
const githubProvider = new GithubAuthProvider();

// Configure Google provider with additional scopes
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Configure GitHub provider
githubProvider.addScope('user:email');

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<{ user: User | null; error: string | null }> => {
  if (!isFirebaseConfigured()) {
    return { user: null, error: 'Firebase is not configured' };
  }

  try {
    const auth = getFirebaseAuth();
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return { user: credential.user, error: null };
  } catch (err) {
    const error = err as AuthError;
    return { user: null, error: getAuthErrorMessage(error.code) };
  }
};

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (
  email: string,
  password: string,
  displayName?: string
): Promise<{ user: User | null; error: string | null }> => {
  if (!isFirebaseConfigured()) {
    return { user: null, error: 'Firebase is not configured' };
  }

  try {
    const auth = getFirebaseAuth();
    const credential = await createUserWithEmailAndPassword(auth, email, password);

    // Update profile with display name if provided
    if (displayName && credential.user) {
      await updateProfile(credential.user, { displayName });
    }

    // Send email verification
    if (credential.user) {
      await sendEmailVerification(credential.user);
    }

    return { user: credential.user, error: null };
  } catch (err) {
    const error = err as AuthError;
    return { user: null, error: getAuthErrorMessage(error.code) };
  }
};

/**
 * Sign in with Google OAuth
 */
export const signInWithGoogle = async (): Promise<{ user: User | null; error: string | null }> => {
  if (!isFirebaseConfigured()) {
    return { user: null, error: 'Firebase is not configured' };
  }

  try {
    const auth = getFirebaseAuth();
    const credential = await signInWithPopup(auth, googleProvider);
    return { user: credential.user, error: null };
  } catch (err) {
    const error = err as AuthError;
    return { user: null, error: getAuthErrorMessage(error.code) };
  }
};

/**
 * Sign in with GitHub OAuth
 */
export const signInWithGitHub = async (): Promise<{ user: User | null; error: string | null }> => {
  if (!isFirebaseConfigured()) {
    return { user: null, error: 'Firebase is not configured' };
  }

  try {
    const auth = getFirebaseAuth();
    const credential = await signInWithPopup(auth, githubProvider);
    return { user: credential.user, error: null };
  } catch (err) {
    const error = err as AuthError;
    return { user: null, error: getAuthErrorMessage(error.code) };
  }
};

/**
 * Sign out the current user
 */
export const signOut = async (): Promise<{ error: string | null }> => {
  if (!isFirebaseConfigured()) {
    return { error: 'Firebase is not configured' };
  }

  try {
    const auth = getFirebaseAuth();
    await firebaseSignOut(auth);
    return { error: null };
  } catch (err) {
    const error = err as AuthError;
    return { error: getAuthErrorMessage(error.code) };
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<{ error: string | null }> => {
  if (!isFirebaseConfigured()) {
    return { error: 'Firebase is not configured' };
  }

  try {
    const auth = getFirebaseAuth();
    await sendPasswordResetEmail(auth, email);
    return { error: null };
  } catch (err) {
    const error = err as AuthError;
    return { error: getAuthErrorMessage(error.code) };
  }
};

/**
 * Get the current user
 */
export const getCurrentUser = (): User | null => {
  if (!isFirebaseConfigured()) {
    return null;
  }
  return getFirebaseAuth().currentUser;
};

/**
 * Subscribe to auth state changes
 */
export const onAuthChange = (callback: (user: User | null) => void): (() => void) => {
  if (!isFirebaseConfigured()) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(getFirebaseAuth(), callback);
};

/**
 * Get user's ID token for API authentication
 */
export const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  const user = getCurrentUser();
  if (!user) return null;

  try {
    return await user.getIdToken(forceRefresh);
  } catch {
    return null;
  }
};

/**
 * Convert Firebase auth error codes to user-friendly messages
 */
const getAuthErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/operation-not-allowed': 'This sign-in method is not enabled.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Invalid credentials. Please check your email and password.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/popup-closed-by-user': 'Sign-in was cancelled.',
    'auth/popup-blocked': 'Pop-up was blocked. Please allow pop-ups for this site.',
    'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials.',
    'auth/requires-recent-login': 'Please sign in again to complete this action.',
    'auth/network-request-failed': 'Network error. Please check your connection.',
  };

  return errorMessages[errorCode] || 'An error occurred. Please try again.';
};
